import {
  ENUM_LABELS,
  type CreateReagentLotInput,
  type Department,
  type ReagentItemStock,
  type Supplier,
} from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { NumberField } from '#client/components/ui/NumberField';
import { Segmented } from '#client/components/ui/Segmented';
import { Select } from '#client/components/ui/Select';
import { TextArea } from '#client/components/ui/TextArea';
import { TextField } from '#client/components/ui/TextField';
import { formatMoney, formatBaseQuantity } from '#client/lib/format';
import { idChoice, nameOptions } from '#client/lib/form-choices';
import { benchOptions, stockAddedBase } from './stock-entry.logic';
import type { SetField } from '#client/hooks/useFormValues';

interface FieldsProps {
  values: CreateReagentLotInput;
  onFieldChange: SetField<CreateReagentLotInput>;
  errors: Record<string, string>;
}

interface BenchCardProps {
  departmentId: string;
  departments: readonly Department[];
  onBenchChange: (departmentId: string) => void;
}

export function BenchCard({ departmentId, departments, onBenchChange }: BenchCardProps) {
  return (
    <Card title="Bench" subtitle="Narrows the reagent list — it is not saved on the entry">
      <Segmented
        label="Department"
        options={benchOptions(departments)}
        value={departmentId}
        onChange={onBenchChange}
      />
    </Card>
  );
}

interface ReagentCardProps extends FieldsProps {
  items: readonly ReagentItemStock[];
  selected: ReagentItemStock | undefined;
}

export function ReagentCard({ values, onFieldChange, errors, items, selected }: ReagentCardProps) {
  return (
    <Card title="Reagent">
      <Select
        label="Reagent"
        options={items.map((item) => ({
          value: String(item.id),
          label: `${item.name} — ${item.pack_label}`,
        }))}
        placeholder="Choose a reagent"
        value={idChoice(values.item_id)}
        error={errors.item_id}
        onChange={(choice) => onFieldChange('item_id', Number(choice))}
        required
      />

      {selected && (
        <dl className="c-summary">
          <div className="c-summary__row">
            <dt>On hand now</dt>
            <dd>
              {formatBaseQuantity(selected.on_hand_base)}{' '}
              {ENUM_LABELS.stockUnit[selected.base_unit]}
            </dd>
          </div>
          <div className="c-summary__row">
            <dt>One pack</dt>
            <dd>
              {formatBaseQuantity(selected.pack_size_base)}{' '}
              {ENUM_LABELS.stockUnit[selected.base_unit]}
            </dd>
          </div>
        </dl>
      )}
    </Card>
  );
}

interface DeliveryCardProps extends FieldsProps {
  suppliers: readonly Supplier[];
  selected: ReagentItemStock | undefined;
}

export function DeliveryCard({
  values,
  onFieldChange,
  errors,
  suppliers,
  selected,
}: DeliveryCardProps) {
  return (
    <Card title="Delivery">
      <div className="c-form-grid">
        <TextField
          label="Lot number"
          value={values.lot_no}
          error={errors.lot_no}
          onChange={(event) => onFieldChange('lot_no', event.target.value)}
          helpText="Use — for a consumable with no vendor batch"
          required
        />
        <TextField
          label="Expiry"
          type="date"
          value={values.expiry_at ?? ''}
          error={errors.expiry_at}
          onChange={(event) => onFieldChange('expiry_at', event.target.value)}
          helpText="Leave blank if it does not expire"
        />
        <NumberField
          label="Packs received"
          min={1}
          value={values.quantity_packs}
          error={errors.quantity_packs}
          onChange={(packs) => onFieldChange('quantity_packs', packs)}
          required
        />
        <NumberField
          label="Price per pack"
          min={0}
          step={0.01}
          value={values.pack_price}
          error={errors.pack_price}
          onChange={(price) => onFieldChange('pack_price', price)}
          prefix="Rs"
          // Asking a receiving clerk for "the cost per microlitre" is how
          // a zero gets typed. The per-unit cost is derived.
          helpText="As invoiced. The per-unit cost is worked out from the pack size."
        />
        <Select
          label="Supplier"
          options={nameOptions(suppliers)}
          placeholder="Not recorded"
          value={idChoice(values.supplier_id)}
          error={errors.supplier_id}
          onChange={(choice) => onFieldChange('supplier_id', choice === '' ? null : Number(choice))}
        />
      </div>

      <TextArea
        label="Note"
        value={values.note ?? ''}
        error={errors.note}
        onChange={(event) => onFieldChange('note', event.target.value)}
        rows={2}
      />

      {selected && (
        <dl className="c-summary">
          <div className="c-summary__row c-summary__row--total">
            <dt>Adds to stock</dt>
            <dd>
              {formatBaseQuantity(stockAddedBase(selected, values.quantity_packs))}{' '}
              {ENUM_LABELS.stockUnit[selected.base_unit]}
            </dd>
          </div>
          <div className="c-summary__row">
            <dt>Total value</dt>
            <dd>{formatMoney(values.quantity_packs * values.pack_price)}</dd>
          </div>
        </dl>
      )}
    </Card>
  );
}
